import { buildLocalizedRecord, type ProjectLocalePolicy } from '../locales/platformLocales';
import type { PlatformVerticalKey } from '../catalog/platformCatalogIds';
import { LAYOUT_COMPONENT_TYPE } from './studio';
import {
  LEGAL_IMPRINT_DEFAULT_LABELS,
  LEGAL_PRIVACY_DEFAULT_LABELS,
  legalFieldLabelContentKey,
} from './legalFieldLabels';

/** System page blocks — always editable regardless of package tier. */
export const SYSTEM_PAGE_COMPONENT_TYPES = [
  LAYOUT_COMPONENT_TYPE.legalImprintBlock,
  LAYOUT_COMPONENT_TYPE.legalPrivacyBlock,
] as const;

export type SystemPageComponentType = (typeof SYSTEM_PAGE_COMPONENT_TYPES)[number];

export const LEGAL_IMPRINT_PDL_FIELD_KEYS = [
  'legalName',
  'representative',
  'address',
  'contactPhone',
  'contactEmail',
  'registerCourt',
  'registerNumber',
  'vatId',
] as const;

/** CDL prose on Imprint. Imported pages use `bodyHtml`, not these rows. */
export const LEGAL_IMPRINT_CDL_FIELD_KEYS = [
  'widerrufText',
  'productQualityText',
] as const;

export const LEGAL_IMPRINT_FIELD_KEYS = [
  ...LEGAL_IMPRINT_PDL_FIELD_KEYS,
  ...LEGAL_IMPRINT_CDL_FIELD_KEYS,
] as const;

/**
 * Imported legal page (sanitized HTML). Not a definition-list row and not a
 * `fieldBindings` role — persona facts inside it use `@legalName` tokens.
 */
export const LEGAL_BODY_HTML_FIELD_KEY = 'bodyHtml' as const;

export const LEGAL_PRIVACY_PDL_FIELD_KEYS = [
  'controllerName',
  'controllerAddress',
  'contactEmail',
] as const;

export const LEGAL_PRIVACY_CDL_FIELD_KEYS = [
  'purposes',
  'legalBasis',
  'retention',
  'rights',
] as const;

export const LEGAL_PRIVACY_FIELD_KEYS = [
  ...LEGAL_PRIVACY_PDL_FIELD_KEYS,
  ...LEGAL_PRIVACY_CDL_FIELD_KEYS,
] as const;

export type LegalImprintFieldKey = (typeof LEGAL_IMPRINT_FIELD_KEYS)[number];
export type LegalPrivacyFieldKey = (typeof LEGAL_PRIVACY_FIELD_KEYS)[number];

/**
 * Definition-list rows.
 * An imported document is the whole page — the field list would repeat it.
 */
export function visibleLegalDefinitionKeys(
  kind: 'impressum' | 'datenschutz',
  bodyHtml: string,
): readonly string[] {
  if (bodyHtml.trim().length > 0) return [];
  return kind === 'impressum' ? LEGAL_IMPRINT_FIELD_KEYS : LEGAL_PRIVACY_FIELD_KEYS;
}

export function isSystemPageComponentType(componentType: string): componentType is SystemPageComponentType {
  return (SYSTEM_PAGE_COMPONENT_TYPES as readonly string[]).includes(componentType);
}

function buildDefaultLegalLabelContent(
  labels: Record<string, Record<string, string>>,
  localePolicy: ProjectLocalePolicy,
): Record<string, Record<string, string>> {
  const { defaultLocale, supportedLocales } = localePolicy;
  const out: Record<string, Record<string, string>> = {};
  for (const [fieldKey, localized] of Object.entries(labels)) {
    out[legalFieldLabelContentKey(fieldKey)] = buildLocalizedRecord(
      defaultLocale,
      supportedLocales,
      localized,
    );
  }
  return out;
}

export function buildDefaultLegalImprintContent(
  businessLabel: string,
  localePolicy: ProjectLocalePolicy,
  vertical: PlatformVerticalKey = 'restaurant',
): Record<string, Record<string, string>> {
  return {
    ...buildDefaultLegalLabelContent(LEGAL_IMPRINT_DEFAULT_LABELS, localePolicy),
    ...buildDefaultLegalImprintCdlContent(vertical, localePolicy),
  };
}

/** CDL-only imprint prose — PDL facts are hydrated at staging from Persona. */
export function buildDefaultLegalImprintCdlContent(
  vertical: PlatformVerticalKey,
  localePolicy: ProjectLocalePolicy,
): Record<string, Record<string, string>> {
  const { defaultLocale, supportedLocales } = localePolicy;
  const widerrufByVertical: Record<PlatformVerticalKey, Record<string, string>> = {
    restaurant: {
      de: 'Für Tischreservierungen und gastronomische Leistungen, die wir vor Ort in unserem Restaurant erbringen, besteht kein Widerrufsrecht nach Fernabsatzrecht. Schließen Sie über unsere Website verbindliche Bestellungen ab (z. B. Gutscheine oder vorbestellte Speisen zur Abholung), gelten die gesetzlichen Widerrufsregelungen, soweit einschlägig — wir weisen Sie darauf vor Vertragsschluss hin.',
      en: 'Table reservations and hospitality services we provide on site are not subject to distance-selling withdrawal. If you place binding orders via our website (e.g. vouchers or pre-ordered takeaway), statutory withdrawal rules apply where relevant — we inform you before the contract is concluded.',
      vi: 'Đặt bàn và dịch vụ tại nhà hàng không thuộc quyền hủy bán từ xa. Nếu đặt hàng ràng buộc qua website (voucher, mang về), quyền hủy theo luật áp dụng khi phù hợp — chúng tôi thông báo trước khi ký kết.',
    },
    cafe: {
      de: 'Bei Getränken und Speisen, die Sie vor Ort an der Theke erwerben, gelten die gesetzlichen Gewährleistungsrechte. Ein Widerrufsrecht nach Fernabsatzrecht besteht bei rein lokalen Verkäufen in der Regel nicht. Für verbindliche Online-Vorbestellungen zur Abholung gelten die gesetzlichen Regelungen, soweit einschlägig.',
      en: 'On-site counter food and drinks are covered by statutory warranty. Distance-selling withdrawal generally does not apply to local sales. Binding online pre-orders for pickup follow statutory rules where applicable.',
      vi: 'Đồ uống và món mua tại quầy được bảo hành theo luật. Hủy bán từ xa thường không áp dụng. Đặt trước online để nhận tại chỗ tuân quy định pháp luật khi phù hợp.',
    },
    bar: {
      de: 'Für vor Ort konsumierte Getränke und Veranstaltungsleistungen gelten die allgemeinen Gewährleistungsvorschriften. Fernabsatz-Widerruf ist bei Leistungen in unseren Räumen in der Regel nicht einschlägig. Online gebuchte Tickets oder Gutscheine unterliegen den jeweiligen Vertragsbedingungen und gesetzlichen Widerrufsregeln.',
      en: 'On-premise drinks and event services follow general warranty rules. Distance withdrawal typically does not apply to in-venue services. Online tickets or vouchers follow their terms and statutory withdrawal where applicable.',
      vi: 'Đồ uống và sự kiện tại chỗ tuân bảo hành chung. Hủy từ xa thường không áp dụng. Vé/voucher online theo điều khoản và luật hủy khi phù hợp.',
    },
    food_truck: {
      de: 'Bei Speisen vom Verkaufsstand gelten die gesetzlichen Gewährleistungsrechte. Ein Widerrufsrecht nach Fernabsatzrecht ist bei reiner Abholung vor Ort regelmäßig ausgeschlossen. Bei verbindlichen Online-Vorbestellungen zur Abholung am Standort gelten die gesetzlichen Regelungen, soweit einschlägig.',
      en: 'Food from the stand is covered by statutory warranty. Distance-selling withdrawal is usually excluded for on-site pickup. Binding online pre-orders for pickup follow statutory rules where applicable.',
      vi: 'Món tại quầy xe tuân bảo hành pháp luật. Hủy bán từ xa thường loại trừ khi nhận tại chỗ. Đặt trước online tuân luật khi phù hợp.',
    },
  };
  const qualityByVertical: Record<PlatformVerticalKey, Record<string, string>> = {
    restaurant: {
      de: 'Wir kennzeichnen Allergene und Zusatzstoffe gemäß der Lebensmittelinformations-Verordnung (LMIV, EU Nr. 1169/2011) in der Speisekarte und auf Nachfrage am Tisch. Bitte sprechen Sie unser Service-Team vor der Bestellung auf Unverträglichkeiten oder besondere Ernährungsbedürfnisse an. Speisen und Getränke werden frisch zubereitet; bei saisonalen Produkten kann die Verfügbarkeit variieren.',
      en: 'We label allergens and additives under the Food Information Regulation (FIR / EU No 1169/2011) on the menu and on request at the table. Please tell our service team about intolerances or special dietary needs before ordering. Dishes are prepared fresh; seasonal availability may vary.',
      vi: 'Chúng tôi ghi dị ứng và phụ gia theo quy định thông tin thực phẩm (LMIV / EU 1169/2011) trên thực đơn và khi được hỏi tại bàn. Vui lòng báo nhân viên về dị ứng hoặc nhu cầu ăn uống trước khi đặt. Món được chế biến tươi; hàng theo mùa có thể thay đổi.',
    },
    cafe: {
      de: 'Kaffee- und Backwaren bieten wir täglich frisch an. Allergenhinweise nach LMIV finden Sie in der Thekenkarte und auf Nachfrage. Bitte weisen Sie unser Team auf Unverträglichkeiten hin.',
      en: 'Coffee and baked goods are offered fresh daily. LMIV allergen notices are on the counter menu and on request. Please tell our team about intolerances.',
      vi: 'Cà phê và bánh phục vụ tươi mỗi ngày. Ghi chú dị ứng LMIV trên thực đơn quầy và khi hỏi. Vui lòng báo nhân viên về dị ứng.',
    },
    bar: {
      de: 'Getränke werden verantwortungsvoll ausgeschenkt; ein Altersnachweis kann verlangt werden. Allergene in Cocktails und Snacks kennzeichnen wir nach LMIV bzw. auf Nachfrage vor der Bestellung.',
      en: 'Drinks are served responsibly; ID may be required. Allergens in cocktails and snacks are labelled under LMIV or on request before ordering.',
      vi: 'Đồ uống phục vụ có trách nhiệm; có thể yêu cầu giấy tờ tùy thân. Dị ứng trong cocktail/snack được ghi theo LMIV hoặc khi hỏi trước khi đặt.',
    },
    food_truck: {
      de: 'Speisen werden frisch zubereitet und zum Mitnehmen angeboten. Hygiene- und Allergenhinweise nach LMIV entnehmen Sie der Speisekarte am Standort bzw. fragen Sie unser Team.',
      en: 'Food is prepared fresh for takeaway. See the on-site menu for LMIV hygiene and allergen information, or ask our team.',
      vi: 'Món chế biến tươi để mang đi. Xem thực đơn tại điểm bán cho vệ sinh/dị ứng LMIV, hoặc hỏi nhân viên.',
    },
  };

  return {
    widerrufText: buildLocalizedRecord(
      defaultLocale,
      supportedLocales,
      widerrufByVertical[vertical] ?? {},
    ),
    productQualityText: buildLocalizedRecord(
      defaultLocale,
      supportedLocales,
      qualityByVertical[vertical] ?? {},
    ),
  };
}

export function buildDefaultLegalPrivacyContent(
  _businessLabel: string,
  localePolicy: ProjectLocalePolicy,
): Record<string, Record<string, string>> {
  return {
    ...buildDefaultLegalLabelContent(LEGAL_PRIVACY_DEFAULT_LABELS, localePolicy),
    ...buildDefaultLegalPrivacyCdlContent(localePolicy),
  };
}

/** CDL-only privacy prose — controller identity is PDL-hydrated. */
export function buildDefaultLegalPrivacyCdlContent(
  localePolicy: ProjectLocalePolicy,
): Record<string, Record<string, string>> {
  const { defaultLocale, supportedLocales } = localePolicy;
  return {
    purposes: buildLocalizedRecord(defaultLocale, supportedLocales, {
      de: 'Wir verarbeiten personenbezogene Daten, um unsere Restaurant-Website bereitzustellen (Speisekarte, Öffnungszeiten, Standort), Online-Reservierungs- und Kontaktanfragen zu beantworten, die technische Sicherheit zu gewährleisten (Server- und Sicherheitsprotokolle) sowie – sofern Sie einwilligen – optionale Analyse- oder Marketing-Cookies zu setzen.',
      en: 'We process personal data to run our restaurant website (menu, hours, location), answer online reservation and contact requests, keep the site technically secure (server and security logs), and — where you consent — set optional analytics or marketing cookies.',
      vi: 'Chúng tôi xử lý dữ liệu để vận hành website nhà hàng (thực đơn, giờ mở cửa, địa điểm), trả lời đặt bàn/liên hệ, bảo mật kỹ thuật (nhật ký máy chủ), và — nếu bạn đồng ý — đặt cookie phân tích/tiếp thị tùy chọn.',
    }),
    legalBasis: buildLocalizedRecord(defaultLocale, supportedLocales, {
      de: 'Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Vertragsanbahnung und -erfüllung, z. B. Reservierungsanfragen), Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an sicherem und funktionsfähigem Website-Betrieb) sowie – für nicht notwendige Cookies – Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).',
      en: 'Processing is based on Art. 6(1)(b) GDPR (pre-contract and contract performance, e.g. reservation requests), Art. 6(1)(f) GDPR (legitimate interest in a secure, working website), and — for non-essential cookies — Art. 6(1)(a) GDPR (consent).',
      vi: 'Xử lý dựa trên Điều 6(1)(b) GDPR (đàm phán/thực hiện hợp đồng, ví dụ đặt bàn), Điều 6(1)(f) (lợi ích hợp pháp về website an toàn), và — với cookie không thiết yếu — Điều 6(1)(a) (đồng ý).',
    }),
    retention: buildLocalizedRecord(defaultLocale, supportedLocales, {
      de: 'Anfragen und Reservierungsdaten speichern wir nur so lange, wie es für die Bearbeitung und etwaige gesetzliche Aufbewahrungspflichten erforderlich ist. Server-Logfiles werden in der Regel nach wenigen Tagen bis Wochen gelöscht oder anonymisiert. Cookie-Laufzeiten richten sich nach Ihrer Einwilligung und den Angaben im Cookie-Hinweis.',
      en: 'We keep enquiry and reservation data only as long as needed for handling and any statutory retention duties. Server logs are usually deleted or anonymised after a few days to weeks. Cookie lifetimes follow your consent and the cookie notice.',
      vi: 'Chúng tôi lưu yêu cầu/đặt bàn chỉ trong thời gian cần xử lý và nghĩa vụ lưu trữ pháp lý. Nhật ký máy chủ thường xóa hoặc ẩn danh sau vài ngày đến vài tuần. Thời hạn cookie theo đồng ý và thông báo cookie.',
    }),
    rights: buildLocalizedRecord(defaultLocale, supportedLocales, {
      de: 'Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung, Datenübertragbarkeit sowie Widerspruch gegen Verarbeitungen auf Basis berechtigter Interessen. Einwilligungen können Sie jederzeit mit Wirkung für die Zukunft widerrufen. Zudem können Sie sich bei einer Datenschutz-Aufsichtsbehörde beschweren — in Bayern etwa beim Bayerischen Landesamt für Datenschutzaufsicht (BayLDA).',
      en: 'You have the right to access, rectification, erasure, restriction, data portability, and to object to processing based on legitimate interests. You may withdraw consent at any time with effect for the future. You may also lodge a complaint with a supervisory authority — in Bavaria, for example, the Bavarian Data Protection Authority (BayLDA).',
      vi: 'Bạn có quyền truy cập, sửa, xóa, hạn chế, chuyển dữ liệu và phản đối xử lý dựa trên lợi ích hợp pháp. Có thể rút đồng ý bất cứ lúc nào cho tương lai. Bạn cũng có thể khiếu nại cơ quan giám sát — tại Bayern ví dụ BayLDA.',
    }),
  };
}
